import React, { useEffect, useState } from 'react';
import {
    Button,
    Col,
    CustomInput,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { listPaises, updateSujeto } from '../api/pldApi';
import FormDomicilio from './FormDomicilio';

function satDateToInput(v) {
    const s = String(v || '').replace(/\D/g, '');
    if (s.length === 8) {
        return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    }
    if (v && String(v).length >= 10) {
        return String(v).slice(0, 10);
    }
    return '';
}

function inputDateToSat(v) {
    return String(v || '').replace(/-/g, '');
}

function hoyInput() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function sugerirCriterioDesdeOrigen(origenBc) {
    const o = String(origenBc || '').toLowerCase();
    if (!o) return '';
    if (o.includes('propiedad') || o.includes('>25') || o.includes('25%')) {
        return 'propiedad';
    }
    if (
        o.includes('administrativ') ||
        o.includes('cargo') ||
        o.includes('descarte') ||
        o.includes('dirección') ||
        o.includes('direccion')
    ) {
        return 'cargo';
    }
    if (o.includes('control') || o.includes('liquidación') || o.includes('liquidacion')) {
        return 'control_directo';
    }
    return 'propiedad';
}

/**
 * Modal de captura del formato «Identificación de Beneficiario Controlador».
 */
export default function ModalFormatoBC({
    unidadId,
    sujeto,
    onClose,
    onSave,
    readOnly = false,
    empresaClienteSugerida = '',
    origenBc = '',
}) {
    const [guardando, setGuardando] = useState(false);
    const [paises, setPaises] = useState([]);

    const [formData, setFormData] = useState({
        pld_bc_empresa_cliente:
            sujeto.pld_bc_empresa_cliente || empresaClienteSugerida || '',
        pld_bc_criterio_control:
            sujeto.pld_bc_criterio_control || sugerirCriterioDesdeOrigen(origenBc),
        nombre: sujeto.nombre || '',
        paterno: sujeto.paterno || '',
        materno: sujeto.materno || '',
        rfc: sujeto.rfc || '',
        curp: sujeto.curp || '',
        pld_fecha_nac_const: satDateToInput(sujeto.pld_fecha_nac_const),
        pld_pais_nacimiento: sujeto.pld_pais_nacimiento || 'MEX',
        pld_nacionalidad: sujeto.pld_nacionalidad || '',
        pld_ocupacion: sujeto.pld_ocupacion || sujeto.pld_actividad_giro_descripcion || '',
        pld_calle: sujeto.pld_calle || '',
        pld_num_ext: sujeto.pld_num_ext || '',
        pld_num_int: sujeto.pld_num_int || '',
        pld_colonia: sujeto.pld_colonia || '',
        pld_cp: sujeto.pld_cp || '',
        pld_ciudad: sujeto.pld_ciudad || '',
        pld_estado: sujeto.pld_estado || '',
        pld_pais_domicilio: sujeto.pld_pais_domicilio || 'MEX',
        pld_telefono: sujeto.pld_telefono || '',
        pld_correo: sujeto.pld_correo || '',
        pld_bc_declarante_nombre: sujeto.pld_bc_declarante_nombre || '',
        pld_bc_fecha_declaracion:
            satDateToInput(sujeto.pld_bc_fecha_declaracion) || hoyInput(),
    });

    useEffect(() => {
        if (!unidadId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const data = await listPaises(unidadId);
                if (!cancelled && Array.isArray(data) && data.length > 0) {
                    setPaises(
                        data.map((p) => ({
                            clave: p.clave || p.id,
                            nombre: p.nombre || p.clave,
                        }))
                    );
                }
            } catch (_) {
                if (!cancelled) setPaises([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [unidadId]);

    async function handleSave() {
        if (readOnly) return;
        if (!formData.pld_bc_empresa_cliente.trim()) {
            await Swal.fire({
                icon: 'warning',
                title: 'Dato requerido',
                text: 'Indique la Empresa / Cliente (persona moral).',
            });
            return;
        }
        if (!formData.pld_bc_criterio_control) {
            await Swal.fire({
                icon: 'warning',
                title: 'Dato requerido',
                text: 'Seleccione el criterio de control.',
            });
            return;
        }
        setGuardando(true);
        const payload = {
            ...formData,
            rfc: String(formData.rfc || '').trim().toUpperCase(),
            curp: String(formData.curp || '').trim().toUpperCase(),
            pld_fecha_nac_const: inputDateToSat(formData.pld_fecha_nac_const),
            pld_bc_fecha_declaracion: inputDateToSat(formData.pld_bc_fecha_declaracion),
            pld_bc_empresa_cliente: formData.pld_bc_empresa_cliente.trim(),
            pld_bc_declarante_nombre: formData.pld_bc_declarante_nombre.trim(),
        };
        try {
            const updated = await updateSujeto(unidadId, sujeto.id, payload);
            if (typeof onSave === 'function') {
                onSave(updated || payload);
            }
            onClose();
        } catch (error) {
            const mensaje =
                (error &&
                    error.response &&
                    error.response.data &&
                    error.response.data.error) ||
                error.message ||
                'Error desconocido';
            await Swal.fire({
                icon: 'error',
                title: 'Error al guardar',
                text: String(mensaje),
            });
        } finally {
            setGuardando(false);
        }
    }

    function setField(field, value) {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }

    return (
        <Modal isOpen toggle={onClose} scrollable backdrop="static" size="xl">
            <ModalHeader className="bg-dark text-white">
                <div className="small text-white-50 font-weight-normal mt-1">
                    <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
                    Identificación de Beneficiario Controlador:
                </div>
                {(sujeto.nombre || '').toUpperCase()} {(sujeto.paterno || '').toUpperCase()}{' '}
                {(sujeto.materno || '').toUpperCase()}
            </ModalHeader>
            <ModalBody className="bg-light">
                <fieldset disabled={readOnly}>
                    <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3">
                        I. Relación con la persona moral (cliente)
                    </h6>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Empresa / Cliente
                                </Label>
                                <Input
                                    value={formData.pld_bc_empresa_cliente}
                                    onChange={(e) =>
                                        setField('pld_bc_empresa_cliente', e.target.value)
                                    }
                                    placeholder="Razón social de la persona moral"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <FormGroup>
                        <Label className="small font-weight-bold text-muted text-uppercase">
                            Criterio de control
                        </Label>
                        <div className="mt-1">
                            <CustomInput
                                type="radio"
                                id="bcCritPropiedad"
                                name="pld_bc_criterio_control"
                                label="Propiedad (Titularidad >25% del capital social)"
                                checked={formData.pld_bc_criterio_control === 'propiedad'}
                                onChange={() => setField('pld_bc_criterio_control', 'propiedad')}
                                className="mb-2"
                            />
                            <CustomInput
                                type="radio"
                                id="bcCritControl"
                                name="pld_bc_criterio_control"
                                label="Control Directo / Voto (Derecho a imponer decisiones o nombrar consejeros)"
                                checked={formData.pld_bc_criterio_control === 'control_directo'}
                                onChange={() =>
                                    setField('pld_bc_criterio_control', 'control_directo')
                                }
                                className="mb-2"
                            />
                            <CustomInput
                                type="radio"
                                id="bcCritCargo"
                                name="pld_bc_criterio_control"
                                label="Cargo (Ejerce la dirección o administración principal)"
                                checked={formData.pld_bc_criterio_control === 'cargo'}
                                onChange={() => setField('pld_bc_criterio_control', 'cargo')}
                            />
                        </div>
                    </FormGroup>

                    <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3 mt-4">
                        II. Datos personales del beneficiario controlador
                    </h6>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Nombre(s)
                                </Label>
                                <Input
                                    value={formData.nombre}
                                    onChange={(e) => setField('nombre', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Apellido paterno
                                </Label>
                                <Input
                                    value={formData.paterno}
                                    onChange={(e) => setField('paterno', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Apellido materno
                                </Label>
                                <Input
                                    value={formData.materno}
                                    onChange={(e) => setField('materno', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    RFC (con homoclave)
                                </Label>
                                <Input
                                    maxLength={13}
                                    className="text-uppercase"
                                    value={formData.rfc}
                                    onChange={(e) =>
                                        setField('rfc', e.target.value.toUpperCase())
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    CURP
                                </Label>
                                <Input
                                    maxLength={18}
                                    className="text-uppercase"
                                    value={formData.curp}
                                    onChange={(e) =>
                                        setField('curp', e.target.value.toUpperCase())
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Fecha de nacimiento
                                </Label>
                                <Input
                                    type="date"
                                    value={formData.pld_fecha_nac_const}
                                    onChange={(e) =>
                                        setField('pld_fecha_nac_const', e.target.value)
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    País de nacimiento
                                </Label>
                                <Input
                                    type="select"
                                    value={formData.pld_pais_nacimiento}
                                    onChange={(e) =>
                                        setField('pld_pais_nacimiento', e.target.value)
                                    }
                                >
                                    {paises.map((p) => (
                                        <option key={p.clave} value={p.clave}>
                                            {p.nombre}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Nacionalidad
                                </Label>
                                <Input
                                    value={formData.pld_nacionalidad}
                                    onChange={(e) =>
                                        setField('pld_nacionalidad', e.target.value)
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Actividad económica u ocupación
                                </Label>
                                <Input
                                    value={formData.pld_ocupacion}
                                    onChange={(e) => setField('pld_ocupacion', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormDomicilio
                        titulo="Domicilio particular"
                        data={formData}
                        paises={paises}
                        onChange={(field, value) => setField(field, value)}
                    />

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Teléfono
                                </Label>
                                <Input
                                    value={formData.pld_telefono}
                                    onChange={(e) => setField('pld_telefono', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="8">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Correo electrónico
                                </Label>
                                <Input
                                    type="email"
                                    value={formData.pld_correo}
                                    onChange={(e) => setField('pld_correo', e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <h6 className="text-primary text-uppercase border-bottom pb-2 mb-3 mt-4">
                        III. Declaración y firma de conformidad
                    </h6>
                    <p className="small text-muted">
                        El que suscribe, en mi carácter de Cliente / Representante Legal o
                        Apoderado, declaro bajo protesta de decir verdad que se me ha requerido
                        la información sobre la existencia de un Dueño Beneficiario /
                        Beneficiario Controlador, y manifiesto que los datos aquí proporcionados
                        sobre su identidad son correctos y veraces.
                    </p>
                    <Row>
                        <Col md="8">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Nombre del Cliente / Representante Legal o Apoderado
                                </Label>
                                <Input
                                    value={formData.pld_bc_declarante_nombre}
                                    onChange={(e) =>
                                        setField('pld_bc_declarante_nombre', e.target.value)
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="small font-weight-bold text-muted text-uppercase">
                                    Fecha
                                </Label>
                                <Input
                                    type="date"
                                    value={formData.pld_bc_fecha_declaracion}
                                    onChange={(e) =>
                                        setField('pld_bc_fecha_declaracion', e.target.value)
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Button
                        color="primary"
                        size="lg"
                        block
                        className="mt-3 font-weight-bold"
                        type="button"
                        disabled={guardando || readOnly}
                        onClick={handleSave}
                    >
                        <FontAwesomeIcon
                            icon={guardando ? faSpinner : faSave}
                            spin={guardando}
                            className="mr-2"
                        />
                        Guardar datos del formato BC
                    </Button>
                </fieldset>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" type="button" onClick={onClose}>
                    Cerrar
                </Button>
            </ModalFooter>
        </Modal>
    );
}
